/**
 * Tâche 1227 (Eric 2026-06-28) — Courriels depuis le pod de TEST.
 *
 * Par défaut, plus aucun courriel ne part du pod de test (transactionnels
 * compris) : chaque itération arrosait les boîtes d'Eric et de Vincent. Cette
 * carte montre le verdict appliqué, permet d'autoriser les envois pendant
 * 60 minutes quand on veut vraiment exercer la chaîne Resend, et liste les
 * derniers courriels bloqués pour vérifier ce qui AURAIT été envoyé.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowClockwise, EnvelopeSimpleOpen } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return String(iso);
  }
};

export const TestEmailGateCard = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sentinel/test-emails`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Tâche 1249 — Données déjà reçues dans l'appel groupé : pas de 2e appel.
  useEffect(() => {
    if (initial) {
      setData(initial);
      setLoading(false);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/api/sentinel/test-emails`,
        { enabled },
        { withCredentials: true },
      );
      setData(r.data);
      toast.success(
        enabled
          ? `Envois autorisés depuis le pod de test pendant ${r.data?.window_minutes ?? 60} minutes.`
          : "Envois bloqués : plus aucun courriel ne part du pod de test.",
      );
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const testPod = data?.is_test_pod !== false;
  const allowed = data?.emails_allowed === true;
  const minutesLeft = Math.ceil((data?.seconds_remaining || 0) / 60);
  const state = loading ? "loading" : !data ? "unknown" : !testPod ? "prod" : allowed ? "open" : "blocked";
  const box = {
    loading: "bg-zinc-50 text-zinc-700 border-zinc-200",
    unknown: "bg-zinc-100 text-zinc-800 border-zinc-300",
    prod: "bg-[#002FA7]/5 text-[#08123A] border-[#002FA7]/30",
    open: "bg-amber-50 text-amber-900 border-amber-300",
    blocked: "bg-emerald-50 text-emerald-900 border-emerald-300",
  }[state];
  const dot = {
    loading: "bg-zinc-300",
    unknown: "bg-zinc-400",
    prod: "bg-[#002FA7]",
    open: "bg-amber-500",
    blocked: "bg-emerald-500",
  }[state];
  const label = {
    loading: "Chargement…",
    unknown: "État inconnu",
    prod: "Pod de PRODUCTION — les courriels partent normalement",
    open: data?.env_allow && !data?.enabled
      ? "Envois AUTORISÉS par ALLOW_PREVIEW_EMAILS=1 (échappatoire d'ops)"
      : `Envois AUTORISÉS pendant encore ${minutesLeft} min (jusqu'à ${fmt(data?.expires_at)})`,
    blocked: "Envois BLOQUÉS — aucun courriel ne part de ce pod de test",
  }[state];

  const suppressed = data?.suppressed || [];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="test-email-gate-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <EnvelopeSimpleOpen size={18} weight="duotone" /> Courriels depuis le pod de test
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
            Par défaut, <strong>aucun</strong> courriel ne quitte le pod de test — invitations
            et réinitialisations de mot de passe incluses. Autorisez-les seulement le temps de
            vérifier la chaîne Resend : l&apos;interrupteur se rééteint tout seul après{" "}
            {data?.window_minutes ?? 60} minutes. Sans effet en production.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-3 py-2 text-sm rounded-lg border border-zinc-300 text-zinc-700 hover:bg-zinc-50 transition-colors flex items-center gap-1.5"
            data-testid="test-email-gate-refresh-btn"
          >
            <ArrowClockwise size={15} /> Rafraîchir
          </button>
          {allowed && testPod ? (
            <button
              onClick={() => toggle(false)}
              disabled={saving}
              className="px-3 py-2 text-sm rounded-lg bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-50 transition-colors"
              data-testid="test-email-gate-block-btn"
            >
              {saving ? "Enregistrement…" : "Bloquer les envois"}
            </button>
          ) : (
            <button
              onClick={() => toggle(true)}
              disabled={saving || !testPod}
              className="px-3 py-2 text-sm rounded-lg bg-[#002FA7] text-white hover:bg-[#002685] disabled:opacity-50 transition-colors"
              data-testid="test-email-gate-allow-btn"
              title={testPod ? "" : "Pod de production : les courriels partent déjà"}
            >
              {saving ? "Enregistrement…" : `Autoriser ${data?.window_minutes ?? 60} min`}
            </button>
          )}
        </div>
      </div>

      <div
        className={`mt-4 rounded-lg border px-4 py-3 flex items-center gap-3 ${box}`}
        data-testid="test-email-gate-state"
      >
        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        <span className="text-sm font-medium">{label}</span>
      </div>

      {data?.updated_by ? (
        <p className="mt-2 text-xs text-zinc-500" data-testid="test-email-gate-updated-by">
          Dernier changement : {data.updated_by} — {fmt(data.updated_at)}
        </p>
      ) : null}

      <div className="mt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Derniers courriels bloqués ({suppressed.length})
        </h3>
        {suppressed.length === 0 ? (
          <p className="text-sm text-zinc-500 mt-2" data-testid="test-email-gate-empty">
            Aucun courriel bloqué pour le moment.
          </p>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm" data-testid="test-email-gate-suppressed-table">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-1.5 pr-3">Quand</th>
                  <th className="text-left py-1.5 pr-3">Destinataire</th>
                  <th className="text-left py-1.5 pr-3">Sujet</th>
                  <th className="text-left py-1.5 hidden sm:table-cell">Motif</th>
                </tr>
              </thead>
              <tbody>
                {suppressed.map((s, i) => (
                  <tr key={`${s.at}-${i}`} className="border-b border-zinc-100 last:border-0">
                    <td className="py-1.5 pr-3 text-zinc-500 whitespace-nowrap">{fmt(s.at)}</td>
                    <td className="py-1.5 pr-3 text-zinc-800 break-all">{s.to}</td>
                    <td className="py-1.5 pr-3 text-zinc-800">{s.subject}</td>
                    <td className="py-1.5 text-zinc-500 hidden sm:table-cell">{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestEmailGateCard;
